/**
 * IFRAP-AI.fr - Universal Scraper Tool
 * Scrape any URL, choose what to do: preview, save to KB, export PDF/Word/Excel
 */

import React, { useState, useEffect } from 'react';
import api from '../../api/client';
import { Globe, Search, Download, Database, FileText, RefreshCw, ExternalLink, Trash2, Eye } from 'lucide-react';

const ACTIONS = [
  { key: 'preview', label: 'Aperçu', icon: Eye, desc: 'Voir le contenu avant de décider' },
  { key: 'save_to_kb', label: 'Base de connaissances', icon: Database, desc: 'Sauvegarder dans la BDD IFRAP' },
  { key: 'export_pdf', label: 'Exporter PDF', icon: FileText, desc: 'Télécharger en PDF' },
  { key: 'export_word', label: 'Exporter Word', icon: FileText, desc: 'Télécharger en .docx' },
  { key: 'export_excel', label: 'Exporter Excel', icon: Download, desc: 'Télécharger en .tsv' },
];

const UniversalScraperPage = () => {
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [action, setAction] = useState('preview');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const res = await api.get('/api/universal-scraper/history');
      setHistory(res.data.results || []);
    } catch (err) {
      console.error('History error:', err);
    }
  };

  const handleScrape = async () => {
    if (!url) return;
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const res = await api.post('/api/universal-scraper/scrape', {
        url,
        action,
        title: title || undefined,
        notes: notes || undefined,
      });

      if (res.data.success) {
        setResult(res.data);

        // If export, trigger download
        if (res.data.export_url) {
          const token = localStorage.getItem('token');
          const link = document.createElement('a');
          link.href = `${process.env.REACT_APP_BACKEND_URL}${res.data.export_url}`;
          link.setAttribute('download', '');
          // For authenticated download, open in new tab
          window.open(`${process.env.REACT_APP_BACKEND_URL}${res.data.export_url}`, '_blank');
        }

        loadHistory();
      } else {
        setError(res.data.message || 'Erreur');
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Erreur de scraping');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-mono text-[#00FF88] flex items-center gap-3">
          <Globe className="w-6 h-6" />
          Scraper Universel
        </h1>
        <p className="text-gray-400 text-sm mt-1">
          Scrapez n'importe quelle URL. Choisissez quoi faire des données.
        </p>
      </div>

      {/* Main Form */}
      <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-6">
        {/* Step 1: URL */}
        <div className="mb-5">
          <label className="flex items-center gap-2 text-sm font-medium text-[#00FF88] mb-2">
            <span className="w-6 h-6 rounded-full bg-[#00FF88] text-black flex items-center justify-center text-xs font-bold">1</span>
            Quelle page voulez-vous scraper ?
          </label>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.exemple.fr/article-interessant"
            className="w-full px-4 py-3 bg-black/30 border border-gray-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-[#00FF88] focus:border-transparent"
            data-testid="universal-scraper-url"
          />
        </div>

        {/* Step 2: Optional title & notes */}
        <div className="grid grid-cols-2 gap-4 mb-5">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Titre personnalisé (optionnel)</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Mon titre..."
              className="w-full px-3 py-2 bg-black/30 border border-gray-700 rounded text-white text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Notes (optionnel)</label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Pourquoi cette page..."
              className="w-full px-3 py-2 bg-black/30 border border-gray-700 rounded text-white text-sm"
            />
          </div>
        </div>

        {/* Step 3: What to do */}
        <div className="mb-5">
          <label className="flex items-center gap-2 text-sm font-medium text-[#00FF88] mb-3">
            <span className="w-6 h-6 rounded-full bg-[#00FF88] text-black flex items-center justify-center text-xs font-bold">2</span>
            Que faire des données scrapées ?
          </label>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {ACTIONS.map((a) => {
              const Icon = a.icon;
              return (
                <button
                  key={a.key}
                  onClick={() => setAction(a.key)}
                  className={`p-3 rounded-lg text-left transition-all border ${
                    action === a.key
                      ? 'bg-[#00FF88]/10 border-[#00FF88] text-white'
                      : 'bg-black/20 border-gray-700 text-gray-400 hover:border-gray-500'
                  }`}
                  data-testid={`action-${a.key}`}
                >
                  <Icon className={`w-5 h-5 mb-1 ${action === a.key ? 'text-[#00FF88]' : ''}`} />
                  <span className="text-xs font-medium block">{a.label}</span>
                  <span className="text-[10px] text-gray-500 block mt-0.5">{a.desc}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Launch */}
        <button
          onClick={handleScrape}
          disabled={loading || !url}
          className="w-full py-3 bg-[#00FF88] text-black font-bold rounded-lg hover:bg-[#00FF88]/90 disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
          data-testid="launch-universal-scrape"
        >
          {loading ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" /> Scraping en cours...
            </>
          ) : (
            <>
              <Search className="w-4 h-4" /> Lancer le scraping
            </>
          )}
        </button>

        {error && (
          <div className="mt-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
            {error}
          </div>
        )}
      </div>

      {/* Result Preview */}
      {result && (
        <div className="bg-[#1a1a2e] border border-[#00FF88]/30 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-white">{result.title || 'Résultat'}</h2>
            {result.message && (
              <span className="text-xs bg-green-500/10 text-green-400 px-3 py-1 rounded-full">
                {result.message}
              </span>
            )}
          </div>

          {result.text_preview && (
            <div className="bg-black/30 rounded-lg p-4 mb-4 max-h-60 overflow-y-auto">
              <p className="text-sm text-gray-300 whitespace-pre-wrap">{result.text_preview}</p>
            </div>
          )}

          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-black/20 rounded p-3">
              <span className="text-xs text-gray-500">Longueur du texte</span>
              <p className="text-lg font-bold text-white">{(result.text_length || 0).toLocaleString('fr-FR')} car.</p>
            </div>
            <div className="bg-black/20 rounded p-3">
              <span className="text-xs text-gray-500">Chiffres clés trouvés</span>
              <p className="text-lg font-bold text-[#FFB000]">{result.key_figures?.length || 0}</p>
            </div>
            <div className="bg-black/20 rounded p-3">
              <span className="text-xs text-gray-500">Liens trouvés</span>
              <p className="text-lg font-bold text-[#00FFFF]">{result.links_count || 0}</p>
            </div>
          </div>

          {/* Key figures */}
          {result.key_figures && result.key_figures.length > 0 && (
            <div className="mb-4">
              <h3 className="text-xs text-gray-400 mb-2 font-medium">Chiffres clés extraits automatiquement</h3>
              <div className="space-y-1">
                {result.key_figures.slice(0, 10).map((fig, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs bg-black/20 rounded p-2">
                    <span className="text-[#FFB000] font-bold whitespace-nowrap">{fig.value} {fig.unit}</span>
                    <span className="text-gray-400 truncate">{fig.context}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick export buttons after preview */}
          {result.result_id && action === 'preview' && (
            <div className="flex gap-2 pt-3 border-t border-gray-700">
              <button
                onClick={() => { setAction('save_to_kb'); handleScrape(); }}
                className="flex items-center gap-1.5 px-3 py-2 bg-[#000091] text-white rounded text-xs hover:bg-[#000091]/80"
              >
                <Database className="w-3 h-3" /> Sauvegarder en BDD
              </button>
              <button
                onClick={() => { setAction('export_word'); handleScrape(); }}
                className="flex items-center gap-1.5 px-3 py-2 bg-gray-700 text-white rounded text-xs hover:bg-gray-600"
              >
                <FileText className="w-3 h-3" /> Export Word
              </button>
              <button
                onClick={() => { setAction('export_pdf'); handleScrape(); }}
                className="flex items-center gap-1.5 px-3 py-2 bg-gray-700 text-white rounded text-xs hover:bg-gray-600"
              >
                <FileText className="w-3 h-3" /> Export PDF
              </button>
              <button
                onClick={() => { setAction('export_excel'); handleScrape(); }}
                className="flex items-center gap-1.5 px-3 py-2 bg-gray-700 text-white rounded text-xs hover:bg-gray-600"
              >
                <Download className="w-3 h-3" /> Export Excel
              </button>
            </div>
          )}
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="bg-[#1a1a2e] border border-gray-800 rounded-lg p-5">
          <h2 className="text-sm font-medium text-gray-400 mb-3">Historique des scrapings ({history.length})</h2>
          <div className="space-y-2">
            {history.slice(0, 15).map((h) => (
              <div key={h.id} className="flex items-center gap-3 bg-black/20 rounded p-3 text-sm">
                <Globe className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-white font-medium truncate block">{h.title}</span>
                  <span className="text-xs text-gray-500 truncate block">{h.url}</span>
                </div>
                <div className="text-right flex-shrink-0">
                  <span className="text-xs text-[#FFB000]">{h.key_figures} chiffres</span>
                  <span className="text-xs text-gray-600 block">{new Date(h.scraped_at).toLocaleString('fr-FR')}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UniversalScraperPage;
